import { z } from 'zod';
import type { IsoDate } from './common.js';
import { emailSchema } from './auth.js';
import { permissionSchema, type Permission } from './permissions.js';
import { taskKeySchema } from './tasks.js';
import type { UserSummary } from './users.js';

export const teamRoles = ['owner', 'admin', 'member'] as const;
export const teamRoleSchema = z.enum(teamRoles);
export type TeamRole = z.infer<typeof teamRoleSchema>;

export const createTeamSchema = z.object({
  name: z.string().trim().min(2, 'Mínimo 2 caracteres').max(50),
});
export type CreateTeamInput = z.infer<typeof createTeamSchema>;

export const updateTeamSchema = createTeamSchema
  .extend({
    taskKey: taskKeySchema,
    /** Shown in the public server directory. */
    description: z.string().trim().max(200).nullable(),
    /** Listed in the public server directory, where anyone can join. */
    isPublic: z.boolean(),
  })
  .partial();
export type UpdateTeamInput = z.infer<typeof updateTeamSchema>;

export interface Team {
  id: string;
  name: string;
  createdAt: IsoDate;
  /** Role of the requesting user in this team. */
  role: TeamRole;
  memberCount: number;
  /** What the requesting user can do in the team (see permissions.ts). */
  permissions: Permission[];
  /** Permissions of the base role «Todos», which every member has. */
  everyonePermissions: Permission[];
  /** Prefix of the team's task keys (`NEX` in `NEX-42`). */
  taskKey: string;
  description: string | null;
  /** Listed in the public server directory. */
  isPublic: boolean;
  /** Nexo's own server, which everyone joins when signing up. */
  official: boolean;
}

/** The person's own order of their teams in the team rail. */
export const reorderTeamsSchema = z.object({ teamIds: z.array(z.uuid()).max(500) });
export type ReorderTeamsInput = z.infer<typeof reorderTeamsSchema>;

/* ---------- Public server directory ---------- */

export interface DirectoryTeam {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  official: boolean;
  /** Highlighted by the Nexo team. */
  featured: boolean;
  /** The requesting user is already a member. */
  joined: boolean;
}

export const directoryQuerySchema = z.object({ q: z.string().trim().max(100).optional() });
export type DirectoryQuery = z.infer<typeof directoryQuerySchema>;

export interface TeamMember extends UserSummary {
  role: TeamRole;
  joinedAt: IsoDate;
  /** Custom roles (labels like "Diseño") assigned to this member. */
  customRoleIds: string[];
}

/* ---------- Member management ---------- */

export const updateMemberSchema = z.object({ role: z.enum(['admin', 'member']) });
export type UpdateMemberInput = z.infer<typeof updateMemberSchema>;

export const transferOwnershipSchema = z.object({ userId: z.uuid() });
export type TransferOwnershipInput = z.infer<typeof transferOwnershipSchema>;

/* ---------- Custom roles ---------- */

export const customRoleColors = [
  'slate',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'sky',
  'indigo',
  'violet',
  'pink',
] as const;
export type CustomRoleColor = (typeof customRoleColors)[number];

export const saveCustomRoleSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Obligatorio')
    .max(30)
    .regex(/^[\p{L}\p{N}][\p{L}\p{N} _.-]*$/u, 'Solo letras, números, espacios, - _ .'),
  color: z.enum(customRoleColors).default('indigo'),
  /** Whether `@role` mentions notify its members. */
  mentionable: z.boolean().default(true),
  permissions: z.array(permissionSchema).max(40).optional(),
});
export type SaveCustomRoleInput = z.input<typeof saveCustomRoleSchema>;

export const setMemberRolesSchema = z.object({ roleIds: z.array(z.uuid()).max(20) });
export type SetMemberRolesInput = z.infer<typeof setMemberRolesSchema>;

export interface CustomRole {
  id: string;
  teamId: string;
  name: string;
  color: CustomRoleColor;
  mentionable: boolean;
  memberCount: number;
  permissions: Permission[];
}

export const createInviteSchema = z.object({
  email: emailSchema,
  role: z.enum(['admin', 'member']).default('member'),
});
export type CreateInviteInput = z.input<typeof createInviteSchema>;

export interface TeamInvite {
  id: string;
  teamId: string;
  email: string;
  role: TeamRole;
  createdAt: IsoDate;
  expiresAt: IsoDate;
  invitedBy: UserSummary;
}

/** Invite as created by an admin: includes the shareable link. */
export interface CreatedInvite extends TeamInvite {
  token: string;
  url: string;
  /** Whether the invitation email could be sent. */
  emailSent: boolean;
}

/** Invite as seen by the person invited. Invites are bound to an email address. */
export interface ReceivedInvite {
  id: string;
  email: string;
  teamId: string;
  teamName: string;
  role: TeamRole;
  invitedBy: UserSummary;
  expiresAt: IsoDate;
  /** False if the invite is for a different email than the current user's. */
  forCurrentUser: boolean;
}

/* ---------- Shareable invite links (anyone with the link can join) ---------- */

export const createInviteLinkSchema = z.object({
  /** Days until it expires; null = never. */
  expiresInDays: z
    .union([z.literal(1), z.literal(7), z.literal(30)])
    .nullable()
    .default(7),
  /** Maximum number of people who can join with it; null = unlimited. */
  maxUses: z.number().int().min(1).max(1000).nullable().default(null),
});
export type CreateInviteLinkInput = z.input<typeof createInviteLinkSchema>;

export interface InviteLink {
  id: string;
  teamId: string;
  url: string;
  role: TeamRole;
  uses: number;
  maxUses: number | null;
  expiresAt: IsoDate | null;
  createdAt: IsoDate;
  createdBy: UserSummary | null;
}

/** What someone opening a join link sees before accepting. */
export interface InviteLinkPreview {
  teamId: string;
  teamName: string;
  memberCount: number;
  invitedBy: UserSummary | null;
  alreadyMember: boolean;
}

/* ---------- Imports from other tools ---------- */

export const importSources = ['slack', 'trello', 'jira'] as const;
export type ImportSource = (typeof importSources)[number];

export interface ImportResult {
  channels: number;
  messages: number;
  tasks: number;
}
